// Locutions — fixed multi-word phrases that behave as a single part of speech
// (di solito = "usually", a poco a poco = "little by little"). Learners should
// memorise them whole. The 30 most common sit in a reference table at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const COMMON = [
  ["di solito", "usually", "Di solito mi alzo presto."],
  ["a volte", "sometimes", "A volte piove."],
  ["di tanto in tanto", "from time to time", "Esco di tanto in tanto."],
  ["ogni tanto", "every now and then", "Ogni tanto lo vedo."],
  ["per esempio", "for example", "Frutta, per esempio le mele."],
  ["senza dubbio", "without a doubt", "Senza dubbio è vero."],
  ["di conseguenza", "consequently", "Piove, di conseguenza resto a casa."],
  ["può darsi", "maybe", "Può darsi che venga."],
  ["all'improvviso", "suddenly", "All'improvviso se ne andò."],
  ["nonostante tutto", "in spite of everything", "Nonostante tutto, sorride."],
  ["per fortuna", "luckily", "Per fortuna è arrivato."],
  ["a proposito", "by the way", "A proposito, e Anna?"],
  ["a partire da", "starting from", "A partire da oggi."],
  ["in mezzo a", "in the middle of", "In mezzo alla folla."],
  ["alla fine", "in the end", "Alla fine ce l'ha fatta."],
  ["in particolare", "in particular", "Mi piace, in particolare il finale."],
  ["vale a dire", "that is to say", "Domani, vale a dire lunedì."],
  ["in altre parole", "in other words", "In altre parole, non vuoi."],
  ["di fatto", "in fact", "Di fatto, lo sapevo già."],
  ["in realtà", "actually", "In realtà non importa."],
  ["tra l'altro", "among other things", "Tra l'altro, ha ragione."],
  ["di nuovo", "again", "Provaci di nuovo."],
  ["non più", "no longer", "Non abita più qui."],
  ["non ancora", "not yet", "Non è ancora arrivato."],
  ["per lo meno", "at least", "Per lo meno ci hai provato."],
  ["prima di tutto", "first of all", "Prima di tutto, grazie."],
  ["invece di", "instead of", "Tè invece di caffè."],
  ["nel frattempo", "in the meantime", "Nel frattempo, aspetta."],
  ["a poco a poco", "little by little", "A poco a poco impari."],
  ["tutto sommato", "all in all", "Tutto sommato, è andata bene."],
];

export function locutionsView(app) {
  const table = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">30 Most Common Locutions</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Italian</th><th class="py-2 px-3">English</th><th class="py-2 px-3">Example</th>
        </tr></thead>
        <tbody>
          ${COMMON
            .map(
              ([es, en, ex]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${es}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
            <td class="py-2 px-3 text-sage-500 italic">${ex}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  renderGuideSections(
    app,
    "Locutions",
    "A locution (locuzione) is a fixed group of words that works as a single unit — one adverb, one preposition, or one conjunction made of several words. Because the meaning lives in the whole phrase, you learn them as set pieces, not word by word.",
    [
      {
        title: "1. What counts as a locution",
        body: `
          <p class="text-sage-700 mb-2">If you can't swap or remove a word without breaking the meaning, you're looking at a locution. <i>A poco a poco</i> means "little by little" as a block — none of <i>a</i> or <i>poco</i> carries that meaning alone.</p>
          <p class="text-sage-700 text-sm">They're the connective tissue of fluent Italian: dropping a few into your speech (<i>di solito</i>, <i>di fatto</i>, <i>di conseguenza</i>) instantly makes it sound more natural.</p>
        `,
      },
      {
        title: "2. The main types",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>Adverbial</b> (act like an adverb): <i>di solito</i> (usually), <i>all'improvviso</i> (suddenly), <i>a poco a poco</i> (little by little).</li>
            <li><b>Prepositional</b> (act like a preposition): <i>invece di</i> (instead of), <i>a causa di</i> (because of), <i>in mezzo a</i> (in the middle of).</li>
            <li><b>Conjunctive</b> (link clauses): <i>ad ogni modo</i> (anyway), <i>di conseguenza</i> (therefore), <i>vale a dire</i> (that is).</li>
            <li><b>Verbal</b> (a verb + fixed words): <i>rendersi conto di</i> (to realise), <i>tenere conto di</i> (to bear in mind), <i>fare a meno di</i> (to do without).</li>
          </ul>
        `,
      },
      {
        title: "3. Using them as connectors",
        body: `
          <p class="text-sage-700 mb-2">Connective locutions usually open or pivot a sentence and are set off with commas:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Ero stanco. <b>Ad ogni modo</b>, ho finito il lavoro.</i></li>
            <li><i>Non ho tempo; <b>di conseguenza</b>, non posso venire.</i></li>
            <li><i><b>Di fatto</b>, l'avevo già letto.</i></li>
          </ul>
          <p class="text-sage-700 text-sm mt-3">Mind the close pairs: <i>cioè</i> ≈ <i>vale a dire</i> (rephrasing), while <i>può darsi</i> ≈ <i>forse</i> (maybe) — but <i>può darsi</i> is followed by the congiuntivo: <i>può darsi che <b>venga</b></i>.</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
